//This file responsible for creating, showing and repeating stroop task

import { ATaskConfiguration, ITaskConfiguration } from './TaskDefinitions';
import { TimelineTask } from './MovingTargetTask';
import { SubEvent } from 'sub-events';
import CONSTANTS from '../../../CONSTANTS/CONSTANTS';
import TaskScene from '../scene/TaskScene';
import Phaser from 'phaser';
import { StroopConfigurationOptions } from '../../../../../commands/practitioner/enums/StroopConfigurationOptions';
import { selectedLanguage } from '../../../CONSTANTS/localization';

export class StroopTaskConfiguration extends ATaskConfiguration {
  mode: boolean = !!StroopConfigurationOptions.None;
  timeBetweenStroopReaction: number = CONSTANTS.stroop.timeBetweenStroopReaction;
  timeToDecreaseStroopReaction: number = CONSTANTS.stroop.timeToDecreaseStroopReaction;

  constructor(seed: number) {
    super(seed);
  }

  public Copy(): StroopTaskConfiguration {
    let config = new StroopTaskConfiguration(this.seed);
    config.enabled = this.enabled;
    config.mode = this.mode;
    return config;
  }

  ApplyDifficulty(difficultyConfig: ITaskConfiguration): void {
    let difConfig = difficultyConfig as StroopTaskConfiguration;
    this.timeBetweenStroopReaction = difConfig.timeBetweenStroopReaction;
    this.timeToDecreaseStroopReaction = difConfig.timeToDecreaseStroopReaction;
  }
}

export class StroopInputEvent {
  public readonly input: boolean;
  public readonly Task: StroopTask;

  constructor(input: boolean, task: StroopTask) {
    this.input = input;
    this.Task = task;
  }
}

export class StroopTask extends TimelineTask<StroopTaskConfiguration> {
  public static readonly STROOP_TIMELINE = 'stroop';
  timelineActive: boolean = false;
  text?: Phaser.GameObjects.Text;
  keyPressesDetected: number = 0;
  readonly OnStroopResult: SubEvent<StroopInputEvent> = new SubEvent();

  public handleStroopButtonPress(): void {
    if (this.timelineActive) this.keyPressesDetected++;
  }

  constructor(configuration: StroopTaskConfiguration, scene: Phaser.Scene) {
    super(configuration, scene, 'StroopTask');
  }

  Create(configuration: StroopTaskConfiguration, scene: Phaser.Scene) {
    this.text = this.createText(scene);
    this.text.setVisible(this.isActive);
    this.OnUpdate(configuration, this.configuration, scene);
  }

  public RebuildAfterResize(scene: Phaser.Scene) {
    const { width, height } = this.GetCurrenScreenSize(scene);
    const center = this.GetPosition(scene);
    const y = center.y / 3 + CONSTANTS.stroop.addPositionY;

    if (this.text) {
      const size = Math.round(this.CalculateScalingFactor(width, height));
      this.text.setStyle({ fontSize: `${size}px`, fontFamily: CONSTANTS.fontStyle as any });
      // force bounds recompute
      this.text.setText(this.text.text);
      this.text.setPosition(center.x, y);
    }
  }

  OnUpdate(oldConfiguration: StroopTaskConfiguration, configuration: StroopTaskConfiguration, scene: Phaser.Scene) {
    this.CleanupInteractionButton();

    this.OnSetVisible(configuration.mode);
    let stroop = configuration.mode ? this.createStroopText(configuration, scene) : scene.tweens.createTimeline();
    this.ReplaceTimeline(StroopTask.STROOP_TIMELINE, stroop);
  }

  Stop(scene: Phaser.Scene): void {
    super.Stop(scene);
    this.CleanupInteractionButton();
  }

  createText(scene: Phaser.Scene) {
    let center = this.GetPosition(scene);
    let y = center.y / 3 + CONSTANTS.stroop.addPositionY;
    let x = center.x;
    const { width, height } = this.GetCurrenScreenSize(scene);

    let fontSettings = `${this.CalculateScalingFactor(width, height)}px ${CONSTANTS.fontStyle}`;

    let text = scene.add
      .text(x, y, '', {
        font: fontSettings,
      })
      .setOrigin(CONSTANTS.stroop.setOrigin);

    return text;
  }

  CleanupInteractionButton() {
    this.keyPressesDetected = 0;
    this.timelineActive = false;
  }

  GetPosition(scene: Phaser.Scene): Phaser.Math.Vector2 {
    const width: any = scene.game.canvas.width;
    const height: any = scene.game.canvas.height;
    const x = width / 2;
    const y = height / 2;
    return new Phaser.Math.Vector2(x, y);
  }

  OnSetVisible(active: boolean): void {
    if (!this.text) {
      return;
    }
    this.text.active = active;
    this.text.setVisible(active);
  }

  createStroopText(configuration: StroopTaskConfiguration, scene: Phaser.Scene) {
    let selectedStroop: undefined | boolean = undefined;
    let stroopDuration = configuration.timeBetweenStroopReaction;
    let center = this.GetPosition(scene);
    this.text?.setVisible(false);
    let timeline = scene.tweens.createTimeline();
    this.text?.setX(center.x);

    let keyboard = scene.input.keyboard;
    let left = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT);
    let isKeyDownPreviousFrame = false;
    let ts = scene as TaskScene;

    const follower = { t: 0 };
    timeline.add({
      targets: follower,
      t: { from: 0, to: 1 },
      duration: stroopDuration,
      onUpdate: () => {
        if (!this.timelineActive) {
          this.timelineActive = true;
        }
        if (!ts.isTablet) {
          if (ts.calledBy === 'client') {
            let isKeyDown = keyboard.checkDown(left);

            if (isKeyDown && !isKeyDownPreviousFrame) {
              this.keyPressesDetected++;
            }

            isKeyDownPreviousFrame = isKeyDown;
          }
        }
      },

      onComplete: () => {
        this.timelineActive = false;
        this.text?.setVisible(false);
        if (selectedStroop !== undefined && ts.calledBy === 'client') {
          if (selectedStroop) {
            this.OnStroopResult.emit(new StroopInputEvent(this.keyPressesDetected === 1, this));
          } else if (!selectedStroop) {
            this.OnStroopResult.emit(new StroopInputEvent(this.keyPressesDetected === 0, this));
          }
        }
      },
    });
    timeline.add({
      targets: follower,
      t: { from: 0, to: 1 },
      duration: stroopDuration,

      onUpdate: () => {
        // Keep aligned with canvas while visible
        const { width, height } = this.GetCurrenScreenSize(scene);
        const ctr = this.GetPosition(scene);
        const yNow = ctr.y / 3 + CONSTANTS.stroop.addPositionY;
        const size = Math.round(this.CalculateScalingFactor(width, height));
        if (this.text) {
          if (Math.round(parseFloat(this.text.style.fontSize as any)) !== size) {
            this.text.setStyle({ fontSize: `${size}px`, fontFamily: CONSTANTS.fontStyle as any });
          }
          this.text.setPosition(ctr.x, yNow);
        }
      },
      onComplete: () => {
        let isRightAnswer = this.random.nextBool();
        if (typeof isRightAnswer === 'number') return;
        selectedStroop = isRightAnswer;

        const text = selectedLanguage();
        const colorsCode = CONSTANTS.stroop.coloursCode;
        this.text?.setVisible(true);
        let maxColorIdx = text.length;

        let randomColorCode = this.random.between(0, maxColorIdx);
        let falseColourArray = text.filter(value => value !== text[randomColorCode]);
        let maxFalseColour = falseColourArray.length;
        let falseColour = this.random.between(0, maxFalseColour);
        // We need a distinct random color and text so we need to make sure that they're different.
        this.text?.setVisible(true);
        this.text?.setText(`${isRightAnswer ? text[randomColorCode] : falseColourArray[falseColour]}`);
        this.text?.setColor(colorsCode[randomColorCode]);

        this.keyPressesDetected = 0;
      },
    });
    timeline.loop = -1;
    return timeline;
  }

  CalculateScalingFactor(screenWidth: number, screenHeight: number) {
    const referenceWidth = CONSTANTS.baseScreenWidth;
    const referenceHeight = CONSTANTS.baseScreenHeight;
    const baseFont = CONSTANTS.fontSize;

    const widthScale = screenWidth / referenceWidth;
    const heightScale = screenHeight / referenceHeight;

    const averageScale = (widthScale + heightScale) / 1.3;

    return baseFont * averageScale;
  }

  GetCurrenScreenSize(scene: Phaser.Scene) {
    const width: number = scene.game.canvas.width;
    let height: number = width * CONSTANTS.movingTarget.heightPercent; // Maintain aspect ratio (height = 60% of width)

    height = Math.min(height, scene.game.canvas.height); // Ensure height does not exceed the actual canvas height

    return { width, height };
  }
}
